import UpdateFlowForm from '@/components/events/detail/tabs/flows/grid/update-flow.form';
import { Moderators } from '@/components/events/detail/tabs/flows/moderators/moderator-widget';
import { CrewWidget } from '@/components/events/detail/tabs/flows/crew/crew-widget';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';
import type { UpdateFlowDto } from '@/types/dtos/flows/request/update-flow.dto';
import { Box, Grid, Stack } from '@mui/material';
import { useState } from 'react';
import FlowSettingsWidget from '@/components/flows/detail/flow-detail-tabs/flow-settings-widget';
import { useGetEventSettingsQuery } from '@/state/queries/events/use-get-event-settings';
import { getObjectId } from '@/helpers/object.id.helper';
import { useSearchParams, useNavigate } from 'react-router';
import { useFlowModeSwitching } from '../use-flow-mode-switching';
import General from '@/components/events/detail/tabs/dashboard/general';
import { useScreenSize } from '@/hooks/use-screen-size';
import { useFlowSyncTransport } from '@/state/mutations/flows/use-flow-sync-transport';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations.tsx';
import type { ContributorListDisplayVariant } from '../flow-detail.types';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import ScopedFlowControls from '@/components/flows/detail/flow-detail-tabs/scoped-flow-controls';
import FlowSubscriptionPlanWidget from '@/components/flows/detail/flow-detail-tabs/flow-subscription-plan-widget';

export default function FlowSettingsTab() {
  const flow = useFlowStore((s) => s.flow);
  const event = useEventStore((s) => s.event);
  const { emitFlowSync, canEmit } = useFlowSyncTransport();
  const notification = useNotifications();
  const t = useTranslations();
  const [updatePending, setUpdatePending] = useState(false);
  const eventOriginId = event?.origin ? getObjectId(event.origin) : event?.id;
  const { data: eventSettings } = useGetEventSettingsQuery(eventOriginId);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventSlotIdFromUrl = searchParams.get('eventSlotId');
  const eventSlotIdParam = searchParams.get('eventSlotId');

  const { isWideScreen } = useScreenSize({ breakpoint: 860 });
  const isSingleFlowEvent = event && !event.multiFlow;
  const isMultiFlowEvent = event?.multiFlow === true;

  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowOrganize = useFlowStore((s) => s.flowOrganize);
  const { currentMode } = useFlowModeSwitching({
    flowId: flow?.origin ? getObjectId(flow.origin) : flow?.id,
    flow,
  });

  const isPerformMode = currentMode === 'PERFORM';

  const { hasAccess: hasMonetizeAccess, isLoading: monetizeAccessLoading } = useFeatureAccess('monetize');

  const openEventSettings = () => {
    const isFlowContext = window.location.pathname.startsWith('/flow/');
    if (isFlowContext) {
      window.dispatchEvent(new CustomEvent('openEventSettings'));
    } else if (event) {
      const eventId = event.origin ? getObjectId(event.origin) : event.id;
      navigate(`/event/${eventId}?tab=dashboard`);
    }
  };

  const openEventSettingsWithDayTiming = (dayIndex: number) => {
    const isFlowContext = window.location.pathname.startsWith('/flow/');
    if (isFlowContext) {
      window.dispatchEvent(new CustomEvent('openEventSettings', { detail: { dayIndex } }));
    } else if (event) {
      const eventId = event.origin ? getObjectId(event.origin) : event.id;
      navigate(`/event/${eventId}?tab=dashboard`);
    }
  };

  const updateFlow = async (flowUpdate: UpdateFlowDto): Promise<void> => {
    setUpdatePending(true);
    try {
      if (!canEmit) {
        notification.notifyError(t('flows.notification.updateError'));
        throw new Error('Flow sync is not available');
      }
      const result = await emitFlowSync({ flow: flowUpdate as Record<string, unknown> });
      if (!result?.ok) {
        notification.notifyError(t('flows.notification.updateError'));
        throw new Error('Flow update was not accepted');
      }
    } finally {
      setUpdatePending(false);
    }
  };

  if (!flow) return null;

  const flowSettingsModeratorsVariant: ContributorListDisplayVariant = 'flowScheduleInline';

  if (!isPerformMode) {
    const eventSlotId = eventSlotIdParam ?? eventSlotIdFromUrl;

    /** Single-flow: event summary. Multi-flow: not shown (flow basic replaces it). */
    const eventBasicWidget = isSingleFlowEvent ? (
      <General
        openSettingsWithDayTiming={openEventSettingsWithDayTiming}
        openSettings={openEventSettings}
        modeOverride={currentMode}
      />
    ) : null;

    /** Multi-flow stand-in for event basic: flow title/description (+ scoped controls). */
    const flowBasicWidget = isMultiFlowEvent ? (
      <Stack spacing={2}>
        <ScopedFlowControls eventSlotId={eventSlotId} />
        <UpdateFlowForm
          flow={flow}
          updatePending={updatePending}
          updateFlow={updateFlow}
          flowPlan={flowPlan}
          flowOrganize={flowOrganize}
          showPublishInHeader={Boolean(eventSlotIdParam)}
        />
      </Stack>
    ) : null;

    const planWidget =
      !monetizeAccessLoading && hasMonetizeAccess ? (
        <FlowSubscriptionPlanWidget flow={flow} eventSlotId={eventSlotId} />
      ) : null;

    /** Media/playout settings — multi-flow only; below plan (wide) / last (narrow). */
    const flowSettingsWidget = isMultiFlowEvent ? (
      <FlowSettingsWidget flow={flow} eventSettings={eventSettings} multiFlow />
    ) : null;

    const hostWidget = (
      <Moderators flow={flow} eventSlotId={eventSlotId ?? undefined} displayVariant={flowSettingsModeratorsVariant} />
    );

    const crewWidget = <CrewWidget variant="flow" flow={flow} eventSlotId={eventSlotId ?? undefined} />;

    return (
      <Box width="100%">
        {isWideScreen ? (
          <Grid container spacing={2}>
            <Grid size={6}>
              <Stack spacing={2}>
                {eventBasicWidget}
                {flowBasicWidget}
                {hostWidget}
                {crewWidget}
              </Stack>
            </Grid>
            <Grid size={6}>
              <Stack spacing={2}>
                {planWidget}
                {flowSettingsWidget}
              </Stack>
            </Grid>
          </Grid>
        ) : (
          <Stack spacing={2}>
            {eventBasicWidget}
            {flowBasicWidget}
            {planWidget}
            {hostWidget}
            {crewWidget}
            {flowSettingsWidget}
          </Stack>
        )}
      </Box>
    );
  }

  return <Box width="100%"></Box>;
}
